import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Flex, Group, Image, LoadingOverlay, Paper, Text } from "@mantine/core";
import { IconUpload } from "@tabler/icons-react";
import { Tenant } from "src/graphql/generated/graphql";

import { useFileInput } from "../../../../../../hooks/fileInput";
import { useUploadImage } from "../../../../../../hooks/files/useUploadImage";
import { useAttachTenantLogo } from "../../../../../../hooks/tenants";

export interface LogoWidgetProps {
    tenant: Tenant;
    onTenantUpdate?();
}

export const LogoWidget = ({ tenant, onTenantUpdate }: LogoWidgetProps) => {
    const { t } = useTranslation(["common"]);

    const { uploadImage, isUploadingImage } = useUploadImage();

    const { attachTenantLogo, isAttachingTenantLogo } = useAttachTenantLogo(tenant?.id);

    const handleLogoUpload = useCallback(
        async (file: File) => {
            const image = await uploadImage(file);
            if (!image?.id) {
                return;
            }

            await attachTenantLogo(image.id);

            onTenantUpdate?.();
        },
        [onTenantUpdate, attachTenantLogo, uploadImage]
    );

    const { fileInputElement: logoInputElement, openFileInputDialog: openLogoInputDialog } = useFileInput({
        onSelect: handleLogoUpload,
    });

    return (
        <Paper pos="relative" p={36} radius={12}>
            <LoadingOverlay visible={isUploadingImage || isAttachingTenantLogo} />
            {logoInputElement}
            <Flex align="center" justify="space-between">
                <Text weight={500}>{t("tenant_logo")}</Text>
                <Group spacing="xs">
                    <Button variant="subtle" leftIcon={<IconUpload size="1em" />} onClick={openLogoInputDialog}>
                        {t("tenant_logo_upload")}
                    </Button>
                </Group>
            </Flex>
            <Box mt="md">{tenant?.logo?.src && <Image src={tenant?.logo?.src} width="auto" mah="72px" />}</Box>
        </Paper>
    );
};
